import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageSeo from "../../components/common/PageSeo";
import { useQueryClient } from "@tanstack/react-query";
import { useUserAuth } from "../../contexts/UserAuthContext";
import { useMobileNav } from "../../contexts/MobileNavContext";
import { useToast } from "../../contexts/ToastContext";
import { useSocket } from "../../contexts/SocketContext";
import api from "../../utils/api/axiosInstance";
import CreatePostModal from "../../components/profile/CreatePostModal";
import PostComposerCard from "../../components/profile/PostComposerCard";
import PostDetailsModal from "../../components/common/PostDetailsModal";
import Modal from "../../components/common/Modal";
import EditPost from "../../components/profile/EditPost";
import AppFeedbackPrompt from "../../components/feedback/AppFeedbackPrompt";
import FeedErrorBoundary from "../../components/common/FeedErrorBoundary";
import { readHomeChatMatchSessionId } from "../../utils/homeChatMatchPersistence";
import { useFeed } from "../../hooks/useFeed";
import { useSuggestedUsers } from "../../hooks/useSuggestedUsers";
import { persistMatchProfile } from "../../utils/matchScoreUtils";
import useActiveFeedMatch from "../../hooks/useActiveFeedMatch";
import useHomeChatMatchResults from "../../hooks/useHomeChatMatchResults";
import useShopperModel from "../../hooks/useShopperModel";
import FeedLeftSidebar from "../../components/feed/FeedLeftSidebar";
import FeedRightSidebar from "../../components/feed/FeedRightSidebar";
import FeedMobileFilters from "../../components/feed/FeedMobileFilters";
import FeedPostsTab from "../../components/feed/FeedPostsTab";
import FeedBattlesTab from "../../components/feed/FeedBattlesTab";
import FeedFlexTab from "../../components/feed/FeedFlexTab";
import FeedAskTab from "../../components/feed/FeedAskTab";
import FeedMatchesTab, {
  FeedMatchesChat,
  MATCH_SHORTLIST_EXHAUSTED_PROMPT,
} from "../../components/feed/FeedMatchesTab";
import { DEDICATED_FEED_POST_TYPES } from "../../utils/feedPostTypeFilters";
import styles from "../../styles/pages/feed/FeedPage.module.css";
import {
  FEED_TAB_PARAM,
  FEED_SOURCE_PARAM,
  MATCH_SESSION_PARAM,
  readFeedTabToken,
  readFeedSourceToken,
  isLegacySourceTabToken,
} from "./feedConstants";
import { useFeedTab } from "./useFeedTab";

/** Main social feed at `/community` (legacy `/feed` redirects here). */
const FeedPage = () => {
  const { user: currentUser } = useUserAuth();
  const { registerCreateHandler } = useMobileNav();
  const [searchParams, setSearchParams] = useSearchParams();
  const { socket, isConnected } = useSocket();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const initialFeedTab = useMemo(
    () => readFeedTabToken(searchParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed once from URL on mount
    [],
  );

  const initialFeedSource = useMemo(
    () => readFeedSourceToken(searchParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed once from URL on mount
    [],
  );

  const {
    posts,
    activeFilter,
    error: feedError,
    loading,
    loadingMore,
    hasMore,
    isRefreshing,
    page,
    fetchFeed,
    loadMore,
    handleFilterChange,
    prependPost,
    removePost,
    updatePost,
  } = useFeed({
    enabled: Boolean(currentUser),
    initialSourceFilter: initialFeedSource,
  });

  const activityUserId = currentUser?._id || currentUser?.id;

  const {
    activeMatch: persistedFeedMatch,
    activeDecision: persistedActiveDecision,
    loading: persistedFeedMatchLoading,
    error: persistedFeedMatchError,
    loadActiveFeedMatch,
    retryLoadActiveFeedMatch,
  } = useActiveFeedMatch(activityUserId, Boolean(activityUserId));

  const { summary: shopperModelSummary } = useShopperModel({
    summaryOnly: true,
    autoFetch: Boolean(activityUserId),
  });

  const shopperCategoryKeys = useMemo(
    () => shopperModelSummary?.categoryKeys || [],
    [shopperModelSummary],
  );

  const { data: suggestedUsers = [] } = useSuggestedUsers({
    limit: 5,
    enabled: Boolean(currentUser),
  });

  const handleFeedSourceClick = useCallback(
    (sourceId) => {
      handleFilterChange(sourceId);
      const next = new URLSearchParams(searchParams);
      if (readFeedTabToken(searchParams) === "matches") {
        next.delete(FEED_TAB_PARAM);
      }
      if (sourceId === "forYou") {
        next.delete(FEED_SOURCE_PARAM);
      } else {
        next.set(FEED_SOURCE_PARAM, sourceId);
      }
      setSearchParams(next, { replace: true });
    },
    [handleFilterChange, searchParams, setSearchParams],
  );

  const {
    activeTab,
    showingMatches,
    showingBattles,
    showingFlex,
    showingAsk,
    setFeedTab,
  } = useFeedTab({
    initialTab: initialFeedTab,
    enabled: Boolean(currentUser),
  });

  useEffect(() => {
    const legacyTab = searchParams.get(FEED_TAB_PARAM);
    if (!isLegacySourceTabToken(legacyTab)) return;

    handleFilterChange(legacyTab);
    const next = new URLSearchParams(searchParams);
    next.delete(FEED_TAB_PARAM);
    if (legacyTab === "following") {
      next.set(FEED_SOURCE_PARAM, "following");
    } else {
      next.delete(FEED_SOURCE_PARAM);
    }
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- migrate legacy ?tab=forYou|following once
  }, []);

  const [chatExpanded, setChatExpanded] = useState(true);
  const [matchCoachPrompt, setMatchCoachPrompt] = useState(null);
  const shortlistExhaustedHandledRef = useRef(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [chatMatchProducts, setChatMatchProducts] = useState([]);
  const [chatMatchSearchParams, setChatMatchSearchParams] = useState(null);
  const [chatMatchLoading, setChatMatchLoading] = useState(false);
  const [matchScoreRevision, setMatchScoreRevision] = useState(0);
  const [userStats, setUserStats] = useState(null);
  const [showCreatePostModal, setShowCreatePostModal] = useState(false);
  const [createPostInitialType, setCreatePostInitialType] = useState("standard");
  const [showEditPostModal, setShowEditPostModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [trendingCategories, setTrendingCategories] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [newPostsCount, setNewPostsCount] = useState(0);
  const [shouldScrollToComments, setShouldScrollToComments] = useState(false);
  const [welcomeDismissed, setWelcomeDismissed] = useState(false);
  const pullStartYRef = useRef(null);
  const isPullingRef = useRef(false);

  const getFeedScrollTop = useCallback(() => window.scrollY, []);

  const scrollFeedToTop = useCallback((behavior = "smooth") => {
    window.scrollTo({ top: 0, behavior });
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 769px)");
    const syncFeedPageRootClass = () => {
      const enabled = mq.matches;
      document.documentElement.classList.toggle("feed-page-root", enabled);
      document.body.classList.toggle("feed-page-root", enabled);
    };
    syncFeedPageRootClass();
    mq.addEventListener("change", syncFeedPageRootClass);
    return () => {
      mq.removeEventListener("change", syncFeedPageRootClass);
      document.documentElement.classList.remove("feed-page-root");
      document.body.classList.remove("feed-page-root");
    };
  }, []);

  const fetchTrendingCategories = useCallback(async () => {
    try {
      const res = await api.get("/categories/trending", {
        params: { limit: 5, days: 7 },
      });
      setTrendingCategories(res.data.categories || []);
    } catch (err) {
      console.error("Error fetching trending categories:", err);
      showToast({
        message: "Couldn't load trending categories",
        type: "error",
      });
    }
  }, [showToast]);

  const fetchUserStats = useCallback(async () => {
    try {
      const userId = currentUser?._id || currentUser?.id;
      const [followersRes, followingRes, postsRes] = await Promise.allSettled([
        api.get("/users/me/followers"),
        api.get("/users/me/following"),
        userId
          ? api.get(`/posts/user/${userId}`, { params: { limit: 1 } })
          : Promise.resolve({ data: { pagination: { total: 0 } } }),
      ]);

      const followers =
        followersRes.status === "fulfilled"
          ? followersRes.value.data.followers || []
          : [];
      const following =
        followingRes.status === "fulfilled"
          ? followingRes.value.data.following || []
          : [];
      const postsTotal =
        postsRes.status === "fulfilled"
          ? postsRes.value.data.pagination?.total || 0
          : 0;

      setUserStats({
        posts: postsTotal,
        followers: followers.length,
        following: following.length,
      });
    } catch (err) {
      console.error("Error fetching user stats:", err);
      showToast({
        message: "Couldn't load your profile stats",
        type: "error",
      });
    }
  }, [currentUser, showToast]);

  useEffect(() => {
    if (!currentUser) return;
    fetchUserStats();
    fetchTrendingCategories();
  }, [currentUser, fetchUserStats, fetchTrendingCategories]);

  useEffect(() => {
    return registerCreateHandler(() => setShowCreatePostModal(true));
  }, [registerCreateHandler]);

  useEffect(() => {
    if (searchParams.get("compose") === "1") {
      setShowCreatePostModal(true);
      const next = new URLSearchParams(searchParams);
      next.delete("compose");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!socket || !isConnected || !currentUser) return undefined;

    const handleNewPost = (payload = {}) => {
      const newPost = payload.post;
      if (!newPost?._id) return;
      if (activeFilter === "following" && DEDICATED_FEED_POST_TYPES.has(newPost.postType)) {
        return;
      }

      if (getFeedScrollTop() < 120) {
        prependPost(newPost);
        return;
      }

      setNewPostsCount((prev) => prev + 1);
    };

    socket.on("feed:new_post", handleNewPost);
    return () => {
      socket.off("feed:new_post", handleNewPost);
    };
  }, [socket, isConnected, currentUser, prependPost, getFeedScrollTop, activeFilter]);

  useEffect(() => {
    if (!currentUser) return;
    const userId = currentUser._id || currentUser.id;
    const key = `feed-welcome-dismissed-${userId}`;
    try {
      setWelcomeDismissed(localStorage.getItem(key) === "1");
    } catch (_) {
      /* private mode */
    }
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser || !showingMatches) return;
    const sessionId = searchParams.get(MATCH_SESSION_PARAM);
    loadActiveFeedMatch(sessionId || null);
  }, [currentUser, showingMatches, searchParams, loadActiveFeedMatch]);

  useEffect(() => {
    if (!persistedFeedMatch?.products?.length) return;
    setChatMatchProducts(persistedFeedMatch.products);
    setChatMatchSearchParams(persistedFeedMatch.searchParams ?? null);
    if (persistedFeedMatch.matchProfile) {
      persistMatchProfile(persistedFeedMatch.matchProfile);
    }
    setMatchScoreRevision((n) => n + 1);
    setChatExpanded(false);
  }, [persistedFeedMatch]);

  const currentUserId = currentUser?._id || currentUser?.id;
  const previousFeedUserIdRef = useRef(undefined);

  useEffect(() => {
    if (!currentUserId) {
      previousFeedUserIdRef.current = currentUserId;
      return;
    }

    if (
      previousFeedUserIdRef.current !== undefined &&
      previousFeedUserIdRef.current !== currentUserId
    ) {
      setChatMatchProducts([]);
      setChatMatchSearchParams(null);
      setChatExpanded(true);
    }

    previousFeedUserIdRef.current = currentUserId;
  }, [currentUserId]);

  useEffect(() => {
    if (!currentUserId || persistedFeedMatchLoading) return;
    if (persistedFeedMatch?.products?.length) return;
    // Don't wipe / re-expand over a local shortlist that just arrived.
    if (chatMatchProducts.length > 0) return;

    setChatMatchProducts([]);
    setChatMatchSearchParams(null);
    setChatExpanded(true);
  }, [
    currentUserId,
    persistedFeedMatch,
    persistedFeedMatchLoading,
    chatMatchProducts.length,
  ]);

  const handlePostDeleted = useCallback(
    (postId) => removePost(postId),
    [removePost],
  );

  const handleLikeUpdate = useCallback(
    (postId, isLiked, likeCount) => {
      updatePost(postId, (p) => ({
        ...p,
        isLiked,
        likeCount,
        likes: isLiked
          ? [...(p.likes || []), currentUser._id]
          : (p.likes || []).filter((id) => id !== currentUser._id),
      }));
    },
    [updatePost, currentUser._id],
  );

  const handleSaveUpdate = useCallback(
    (postId, isSaved) => {
      updatePost(postId, (p) => ({ ...p, isSaved }));
    },
    [updatePost],
  );

  const handleCommentUpdate = useCallback(
    (postId, commentCount) => {
      updatePost(postId, (p) => ({ ...p, commentCount }));
    },
    [updatePost],
  );

  const handlePostUpdated = useCallback(
    (updatedPost) => {
      updatePost(updatedPost._id, () => updatedPost);
    },
    [updatePost],
  );

  const handleEditPost = useCallback((post) => {
    setEditingPost(post);
    setShowEditPostModal(true);
  }, []);

  const handleCommentOpen = useCallback((post) => {
    setSelectedPost(post);
    setShouldScrollToComments(true);
  }, []);

  const handleVoteUpdate = useCallback(
    (postId, comparisonStats) => {
      updatePost(postId, (p) => ({ ...p, comparisonStats }));
      setSelectedPost((prev) =>
        prev && prev._id === postId ? { ...prev, comparisonStats } : prev,
      );
    },
    [updatePost],
  );

  const handlePostCreated = useCallback(
    (newPost) => {
      if (!newPost) return;
      if (
        activeFilter !== "following" ||
        !DEDICATED_FEED_POST_TYPES.has(newPost.postType)
      ) {
        prependPost(newPost);
      }
      setUserStats((prev) =>
        prev ? { ...prev, posts: (prev.posts || 0) + 1 } : prev,
      );
    },
    [activeFilter, prependPost],
  );

  const handleOpenPost = useCallback((post, event) => {
    setShouldScrollToComments(false);
    const target = event.target;
    const tagName = target.tagName.toLowerCase();
    const interactiveTags = [
      "button",
      "a",
      "input",
      "textarea",
      "select",
      "label",
    ];

    if (
      interactiveTags.includes(tagName) ||
      target.closest("button, a, input, textarea, select, label")
    ) {
      return;
    }
    setSelectedPost(post);
  }, []);

  const handleFollowUser = useCallback(
    async (userId) => {
      try {
        await api.post(`/users/${userId}/follow`);
        queryClient.setQueryData(["suggestedUsers", 5], (prev) =>
          (prev || []).filter((u) => u._id !== userId),
        );
        fetchUserStats();
      } catch (err) {
        console.error("Error following user:", err);
        showToast({
          message: err.response?.data?.message || "Failed to follow user",
          type: "error",
        });
      }
    },
    [queryClient, fetchUserStats, showToast],
  );

  const handleChatMatchResults = useHomeChatMatchResults({
    activityUserId,
    loadActiveFeedMatch,
    setChatMatchLoading,
    setChatMatchSearchParams,
    setChatMatchProducts,
    setMatchScoreRevision,
    setChatExpanded,
    variant: "feed",
    searchParams,
    setSearchParams,
  });

  useEffect(() => {
    shortlistExhaustedHandledRef.current = false;
    setMatchCoachPrompt(null);
  }, [chatMatchProducts]);

  const handleShortlistExhausted = useCallback(() => {
    if (shortlistExhaustedHandledRef.current) return;
    shortlistExhaustedHandledRef.current = true;
    setChatExpanded(true);
    setMatchCoachPrompt(MATCH_SHORTLIST_EXHAUSTED_PROMPT);
    scrollFeedToTop("smooth");
  }, [scrollFeedToTop]);

  const handleCoachPromptConsumed = useCallback(() => {
    setMatchCoachPrompt(null);
  }, []);

  const handleProfileClick = useCallback(() => {
    const userId = currentUser?._id || currentUser?.id;
    if (userId) {
      navigate(`/user/${userId}`);
    }
  }, [currentUser, navigate]);

  const isFirstTimeUser =
    !!userStats && userStats.followers === 0 && userStats.following === 0;

  const dismissWelcomeBanner = useCallback(() => {
    const userId = currentUser?._id || currentUser?.id;
    if (userId) {
      try {
        localStorage.setItem(`feed-welcome-dismissed-${userId}`, "1");
      } catch (_) {
        /* private mode or storage quota */
      }
    }
    setWelcomeDismissed(true);
  }, [currentUser]);

  const openCreatePostModal = useCallback((postType = "standard") => {
    setCreatePostInitialType(postType);
    setShowCreatePostModal(true);
  }, []);

  const handleCreateBattle = useCallback(
    () => openCreatePostModal("battle"),
    [openCreatePostModal],
  );
  const handleCreateFlexPost = useCallback(
    () => openCreatePostModal("flex"),
    [openCreatePostModal],
  );
  const handleCreateAskPost = useCallback(
    () => openCreatePostModal("question"),
    [openCreatePostModal],
  );
  const handleNewPostsRefresh = useCallback(async () => {
    await fetchFeed(1, { isRefresh: true });
    setNewPostsCount(0);
    scrollFeedToTop();
  }, [fetchFeed, scrollFeedToTop]);

  const showingPostsFeed =
    !showingMatches && !showingBattles && !showingFlex && !showingAsk;

  return (
    <FeedErrorBoundary onRetry={() => fetchFeed(1, { isRefresh: true })}>
      <PageSeo
        title="Community"
        description="Your personalized home — posts, comparisons, and trending gear from the community."
        pathname="/community"
        noIndex
      />
      <div
        className={`pageContainer ${styles.feedPageShell}`}
        onTouchStart={(event) => {
          if (getFeedScrollTop() === 0 && !loading && !loadingMore) {
            pullStartYRef.current = event.touches[0].clientY;
            isPullingRef.current = true;
          }
        }}
        onTouchMove={(event) => {
          if (!isPullingRef.current || pullStartYRef.current === null) {
            return;
          }
          const currentY = event.touches[0].clientY;
          const distance = Math.max(0, currentY - pullStartYRef.current);
          if (distance > 0) {
            setPullDistance(Math.min(distance, 120));
          }
        }}
        onTouchEnd={() => {
          if (pullDistance > 70) {
            fetchFeed(1, { isRefresh: true });
          }
          pullStartYRef.current = null;
          isPullingRef.current = false;
          setPullDistance(0);
        }}
      >
        <div className={`innerContainer ${styles.feedPageInner}`}>
          <div
            className={`${styles.pullToRefresh} ${
              isRefreshing ? styles.pullToRefreshActive : ""
            }`}
            style={{ height: `${pullDistance}px` }}
            role="status"
            aria-live="polite"
          >
            <span>
              {isRefreshing
                ? "Refreshing..."
                : pullDistance > 70
                  ? "Release to refresh"
                  : "Pull to refresh"}
            </span>
          </div>
          <div className={styles.feedLayout}>
            <FeedLeftSidebar
              currentUser={currentUser}
              activeFilter={activeFilter}
              activeTab={activeTab}
              showingMatches={showingMatches}
              showingBattles={showingBattles}
              showingFlex={showingFlex}
              showingAsk={showingAsk}
              onProfileClick={handleProfileClick}
              onFeedSourceClick={handleFeedSourceClick}
              onFeedTab={setFeedTab}
            />

            <section className={styles.feedMain}>
              {showingMatches ? (
                <FeedMatchesChat
                  chatExpanded={chatExpanded}
                  onChatExpandedChange={setChatExpanded}
                  onMatchResults={handleChatMatchResults}
                  coachPrompt={matchCoachPrompt}
                  onCoachPromptConsumed={handleCoachPromptConsumed}
                  hasResults={chatMatchProducts.length > 0 && !chatMatchLoading}
                  searchParams={chatMatchSearchParams}
                />
              ) : (
                <PostComposerCard
                  user={currentUser}
                  centered
                  onCompose={(postType) => openCreatePostModal(postType)}
                />
              )}

              <FeedMobileFilters
                activeFilter={activeFilter}
                activeTab={activeTab}
                showingMatches={showingMatches}
                showingBattles={showingBattles}
                showingFlex={showingFlex}
                showingAsk={showingAsk}
                onFeedSourceClick={handleFeedSourceClick}
                onFeedTab={setFeedTab}
              />

              <FeedMatchesTab
                active={showingMatches}
                products={chatMatchProducts}
                matchLoading={chatMatchLoading || persistedFeedMatchLoading}
                searchParams={chatMatchSearchParams}
                matchScoreRevision={matchScoreRevision}
                activeDecision={persistedActiveDecision}
                onShortlistExhausted={handleShortlistExhausted}
                loadError={persistedFeedMatchError}
                onRetryLoad={retryLoadActiveFeedMatch}
              />

              <FeedBattlesTab
                active={showingBattles}
                activeFilter={activeFilter}
                onCreateBattle={handleCreateBattle}
                onOpenPost={handleOpenPost}
                onPostDeleted={handlePostDeleted}
                onLikeUpdate={handleLikeUpdate}
                onSaveUpdate={handleSaveUpdate}
                onCommentUpdate={handleCommentUpdate}
                onVoteUpdate={handleVoteUpdate}
                onPostUpdated={handlePostUpdated}
                onEditPost={handleEditPost}
                onCommentOpen={handleCommentOpen}
                activeDecision={persistedActiveDecision}
                shopperCategoryKeys={shopperCategoryKeys}
              />

              <FeedFlexTab
                active={showingFlex}
                activeFilter={activeFilter}
                onCreateFlexPost={handleCreateFlexPost}
                onOpenPost={handleOpenPost}
                onPostDeleted={handlePostDeleted}
                onLikeUpdate={handleLikeUpdate}
                onSaveUpdate={handleSaveUpdate}
                onCommentUpdate={handleCommentUpdate}
                onVoteUpdate={handleVoteUpdate}
                onPostUpdated={handlePostUpdated}
                onEditPost={handleEditPost}
                onCommentOpen={handleCommentOpen}
                activeDecision={persistedActiveDecision}
                shopperCategoryKeys={shopperCategoryKeys}
              />

              <FeedAskTab
                active={showingAsk}
                activeFilter={activeFilter}
                onCreateAskPost={handleCreateAskPost}
                onOpenPost={handleOpenPost}
                onPostDeleted={handlePostDeleted}
                onLikeUpdate={handleLikeUpdate}
                onSaveUpdate={handleSaveUpdate}
                onCommentUpdate={handleCommentUpdate}
                onVoteUpdate={handleVoteUpdate}
                onPostUpdated={handlePostUpdated}
                onEditPost={handleEditPost}
                onCommentOpen={handleCommentOpen}
                activeDecision={persistedActiveDecision}
                shopperCategoryKeys={shopperCategoryKeys}
              />

              <FeedPostsTab
                active={showingPostsFeed}
                posts={posts}
                activeFilter={activeFilter}
                activeTab={activeTab}
                feedError={feedError}
                loading={loading}
                loadingMore={loadingMore}
                hasMore={hasMore}
                page={page}
                isRefreshing={isRefreshing}
                loadMore={loadMore}
                fetchFeed={fetchFeed}
                newPostsCount={newPostsCount}
                onNewPostsRefresh={handleNewPostsRefresh}
                isFirstTimeUser={isFirstTimeUser}
                welcomeDismissed={welcomeDismissed}
                onDismissWelcome={dismissWelcomeBanner}
                suggestedUsers={suggestedUsers}
                trendingCategories={trendingCategories}
                onFollowUser={handleFollowUser}
                onOpenPost={handleOpenPost}
                onPostDeleted={handlePostDeleted}
                onLikeUpdate={handleLikeUpdate}
                onSaveUpdate={handleSaveUpdate}
                onCommentUpdate={handleCommentUpdate}
                onVoteUpdate={handleVoteUpdate}
                onPostUpdated={handlePostUpdated}
                onEditPost={handleEditPost}
                onCommentOpen={handleCommentOpen}
                activeDecision={persistedActiveDecision}
                shopperCategoryKeys={shopperCategoryKeys}
              />

              <AppFeedbackPrompt
                source="feed"
                prompt="How is the community feed working for you?"
                metadata={{ sessionId: readHomeChatMatchSessionId() }}
              />
            </section>

            <FeedRightSidebar currentUser={currentUser} />
          </div>
        </div>

        <CreatePostModal
          isOpen={showCreatePostModal}
          onClose={() => {
            setShowCreatePostModal(false);
            setCreatePostInitialType("standard");
          }}
          onPostCreated={handlePostCreated}
          initialPostType={createPostInitialType}
        />
        <PostDetailsModal
          post={selectedPost}
          isOpen={Boolean(selectedPost)}
          onClose={() => {
            setSelectedPost(null);
            setShouldScrollToComments(false);
          }}
          scrollToComments={shouldScrollToComments}
          onPostUpdated={(updatedPost) => {
            handlePostUpdated(updatedPost);
            setSelectedPost(updatedPost);
          }}
          onPostDeleted={(deletedPostId) => {
            handlePostDeleted(deletedPostId);
            setSelectedPost(null);
          }}
          onVoteUpdate={handleVoteUpdate}
          onLikeUpdate={handleLikeUpdate}
          onSaveUpdate={handleSaveUpdate}
          onCommentUpdate={handleCommentUpdate}
          activeDecision={persistedActiveDecision}
          shopperCategoryKeys={shopperCategoryKeys}
        />
        <Modal
          isOpen={showEditPostModal}
          onClose={() => {
            setShowEditPostModal(false);
            setEditingPost(null);
          }}
          ariaLabel="Edit post"
        >
          {editingPost && (
            <EditPost
              post={editingPost}
              onPostUpdated={(updatedPost) => {
                handlePostUpdated(updatedPost);
                setShowEditPostModal(false);
                setEditingPost(null);
              }}
              onCancel={() => {
                setShowEditPostModal(false);
                setEditingPost(null);
              }}
            />
          )}
        </Modal>

      </div>
    </FeedErrorBoundary>
  );
};

export default FeedPage;
